import Loading from '@doordash/component-loading'
import { Modal } from '@doordash/component-modal'
import Separator from '@doordash/component-separator'
import { Text, Stack } from '@doordash/design-language'
import { isFeatureFlagEnabled } from '@doordash/lib-feature-flags'
import * as React from 'react'
import { connect } from 'react-redux'
import * as actions from '../../../actions'
import {
  ConfirmationStepKeys,
  ConfirmationStepTrackingKeys,
} from '../../../constants'
import * as events from '../../../events'
import {
  Applicant,
  ApplicationStatus,
  ShippingStatus,
  StripeCredentials,
} from '../../../models/types'
import { detectWebviewOs } from '../../../utilities/userAgent'
import { OsTypes } from '../../../webview/types'
import { loadStripe } from '../../atoms/stripe'
import ListItem from '../../molecules/list-item'
import RedCard from '../../organisms/redcard'
import BankAdditionalVerification from '../bank-additional-verification'
import ActivationKitStep from './activation-kit-step'
import BackgroundCheckStep from './background-check-step'
import PaymentSetupStep from './payment-setup-step'
import RedCardActivationStep from './red-card-activation-step'
import StartDashingStep from './start-dashing-step'
import FastPayStep from './fast-pay-step'
import BoFWaitlist from '../bof-waitlist'
import { showBoFWaitlistSelector } from '../../../selectors/bottomOfFunnelWaitlist'

import { DX_ID_FAST_PAY_Q1_2020 } from '../../../experimentConstants'

import { get } from 'lodash'
import { Pages } from './constants'

import t from './strings'

type DropshippingStatusProps = {
  application: object
  uniqueLink: string
  isLoading: boolean
  applicationStatus: ApplicationStatus
  experiments: any
  dasherId: string
  stripeCredentials: StripeCredentials
  getApplicationStatus?: (uniqueLink: string) => any
  trackConfirmationPageRendered?: (data: object) => Promise<any>
  trackDropshippingEvent?: (name: string) => Promise<any>
  trackConfirmationPageSection?: (
    stepName: string,
    data: object
  ) => Promise<any>
  getStripeCredentials: (dasherId: string) => Promise<any>
  getPaymentAccount?: (dasherId: string) => Promise<any>
}

const isBoFWaitlisted = (onboardingProgress, experiments) => {
  const os = detectWebviewOs()
  const isWebview = os !== OsTypes.NONE

  const backgroundCheckComplete = get(
    onboardingProgress,
    'backgroundCheckComplete',
    false
  )
  const paymentAccountSetupComplete = get(
    onboardingProgress,
    'paymentAccountSetupComplete',
    false
  )
  const firstDeliveryComplete = get(
    onboardingProgress,
    'firstDeliveryComplete',
    false
  )
  if (
    showBoFWaitlistSelector({
      isWebview,
      backgroundCheckComplete,
      paymentAccountSetupComplete,
      firstDeliveryComplete,
      bofWaitlistExperiment: experiments.dx_bof_waitlist,
    })
  ) {
    return true
  }

  return false
}

export class DropshippingStatus extends React.Component<
  DropshippingStatusProps
> {
  public static defaultProps = {
    isLoading: true,
    getApplicationStatus: console.log,
    trackConfirmationPageRendered: console.log,
    trackConfirmationPageSection: console.log,
    trackDropshippingEvent: console.log,
  }

  public state = {
    page: Pages.Status,
    stripe: null,
    kycFlow: true,
  }

  private applicant: Applicant

  public constructor(props) {
    super(props)
    this.applicant = {
      uniqueLink: props.uniqueLink,
      firstName: props.application.firstName,
      email: props.application.email,
      backgroundCheckCompletionUrl:
        props.application.backgroundCheckCompletionUrl,
    }
  }

  public redCardLink = () => {
    this.setState({ page: Pages.RedCard })
  }

  public returnLink = () => {
    this.setState({ page: Pages.Status })
  }

  public handleFastPaySkip = () => {
    const { onboardingProgress } = this.props.applicationStatus
    onboardingProgress[ConfirmationStepKeys.FASTPAY] = true

    // Hacky... but it works for now
    this.setState({})
  }

  public async componentDidMount() {
    const {
      applicationStatus: { onboardingProgress },
      dasherId,
      trackConfirmationPageRendered,
      trackDropshippingEvent,
      stripeCredentials,
      getStripeCredentials,
      getPaymentAccount,
      getApplicationStatus,
      experiments,
    } = this.props

    if (dasherId && this.state.stripe === null && experiments.bank_onboarding) {
      await getStripeCredentials(dasherId)
      await getPaymentAccount(dasherId)
    }

    if (stripeCredentials.publicKey) {
      loadStripe(this.setStripe, stripeCredentials.publicKey)
    }

    await getApplicationStatus(this.applicant.uniqueLink)

    // Should not run these tracking events if the user
    // is bottom-of-funnel waitlisted
    if (!isBoFWaitlisted(onboardingProgress, experiments)) {
      trackConfirmationPageRendered({
        active_step: this.currentStep(),
      })
      trackDropshippingEvent('DA_dxds_confirmation_page')
    }
  }

  public componentDidUpdate(prevProps) {
    // fetch stripe after fetching stripe credentials
    if (
      this.props.stripeCredentials !== prevProps.stripeCredentials &&
      this.props.stripeCredentials.publicKey
    ) {
      loadStripe(this.setStripe, this.props.stripeCredentials.publicKey)
    }
  }

  public currentStep() {
    const { onboardingProgress, shippingStatus } = this.props.applicationStatus
    if (!onboardingProgress[ConfirmationStepKeys.BACKGROUNDCHECK]) {
      return 1
    }

    if (onboardingProgress[ConfirmationStepKeys.REDCARDACTIVATION]) {
      return 4
    }
    if (
      onboardingProgress[ConfirmationStepKeys.PAYMENTACCOUNTSETUP] ||
      shippingStatus === ShippingStatus.Delivered
    ) {
      return 3
    }
    return 2
  }

  public render() {
    const {
      application,
      applicationStatus: {
        step,
        bgcheckStatus,
        shippingAddress,
        shippingArrivalDate,
        shippingStatus,
        shippingUrl,
        onboardingProgress,
        verificationStatus,
      },
      isLoading,
      experiments,
      trackConfirmationPageSection,
      uniqueLink,
    } = this.props

    const os = detectWebviewOs()
    const isWebview = os !== OsTypes.NONE

    const { page, kycFlow, stripe } = this.state

    const dasherId = get(application, 'dasherId', null)

    if (isLoading) {
      return <Loading />
    }

    /**
     * Show the Bottom-of-Funnel waitlist only if background check
     * and payment account setup are complete, and first delivery is
     * NOT complete.
     * We want users to be able to still be able to complete onboarding
     * tasks until they are shown the waitlist screen.
     */
    if (isBoFWaitlisted(onboardingProgress, experiments)) {
      return <BoFWaitlist />
    }

    const Steps = [
      <BackgroundCheckStep
        status={bgcheckStatus}
        email={this.applicant.email}
        backgroundCheckCompletionUrl={
          this.applicant.backgroundCheckCompletionUrl
        }
        step={ConfirmationStepTrackingKeys.BACKGROUNDCHECK}
        completeKey={ConfirmationStepKeys.BACKGROUNDCHECK}
        generateTitle={isComplete =>
          isComplete ? t.backgroundCheckComplete() : t.backgroundCheckStep()
        }
      />,
      <ActivationKitStep
        generateTitle={t.activationKitStep}
        address={shippingAddress}
        shippingArrivalDate={shippingArrivalDate}
        shippingStatus={shippingStatus}
        shippingUrl={shippingUrl}
        bgcheckStatus={bgcheckStatus}
        step={ConfirmationStepTrackingKeys.ACTIVATIONKIT}
        completeKey={ConfirmationStepKeys.ACTIVATIONKIT}
      />,
      <RedCardActivationStep
        generateTitle={isComplete =>
          isComplete ? t.redCardEntryComplete() : t.redCardEntryStep()
        }
        address={shippingAddress}
        shippingArrivalDate={shippingArrivalDate}
        shippingStatus={shippingStatus}
        shippingUrl={shippingUrl}
        bgcheckStatus={bgcheckStatus}
        pageLink={this.redCardLink}
        returnLink={this.returnLink}
        step={ConfirmationStepTrackingKeys.REDCARDACTIVATION}
        experiments={experiments}
        completeKey={ConfirmationStepKeys.REDCARDACTIVATION}
      />,
      <StartDashingStep
        generateTitle={t.startDashingStep}
        step={ConfirmationStepTrackingKeys.STARTDASHING}
        completeKey={ConfirmationStepKeys.STARTDASHING}
      />,
    ]

    if (experiments.instant_dashing_v2) {
      if (
        isWebview
          ? experiments.dx_id_fast_pay_q1_2020
          : isFeatureFlagEnabled(DX_ID_FAST_PAY_Q1_2020)
      ) {
        Steps[2] = (
          <FastPayStep
            completeKey={ConfirmationStepKeys.FASTPAY}
            step={ConfirmationStepTrackingKeys.FASTPAY}
            generateTitle={isComplete =>
              isComplete ? t.fastPayComplete() : t.fastPayStep()
            }
            stripe={stripe}
            skipStep={this.handleFastPaySkip}
            badgeText={t.optional()}
            isPaymentAccountSetup={
              onboardingProgress[ConfirmationStepKeys.PAYMENTACCOUNTSETUP]
            }
          />
        )
      } else {
        // removing redcard step from list items if in instant dashing v2
        // but
        Steps.splice(2, 1)
      }
    }

    if (experiments.bank_onboarding) {
      // This is just to maintain the old flow for experiments
      // so we're shifting steps around here
      Steps[1] = (
        <PaymentSetupStep
          generateTitle={isComplete =>
            isComplete ? t.addBankAccountComplete() : t.addBankAccountStep()
          }
          completeKey={ConfirmationStepKeys.PAYMENTACCOUNTSETUP}
          step={ConfirmationStepTrackingKeys.PAYMENTACCOUNTSETUP}
          uniqueLink={this.props.uniqueLink}
          stripe={stripe}
        />
      )
      const needsPhotoButPastBankSetup =
        verificationStatus.documentsRequired &&
        verificationStatus.documentsRequired.includes(
          'legal_entity.verification.document'
        ) &&
        onboardingProgress[ConfirmationStepKeys.PAYMENTACCOUNTSETUP]

      // Additional Verification (KYC) Flow
      // Pop up this modal only if we need verification from verificationStatus
      // kycFlow default to true but set to false to dismiss the modal
      if (
        kycFlow &&
        verificationStatus.documentsRequired &&
        verificationStatus.documentsRequired.length > 0 &&
        (verificationStatus.needsVerification || needsPhotoButPastBankSetup)
      ) {
        return (
          <Modal
            isFullScreenOnMobile={true}
            hasCloseButton={false}
            size={Modal.Sizes.Small}
          >
            <BankAdditionalVerification
              handleDismiss={() => this.setState({ kycFlow: false })}
              isDismissible={verificationStatus.isDismissible}
              uniqueLink={uniqueLink}
              documentsRequired={verificationStatus.documentsRequired}
              skipIntro={!verificationStatus.needsVerification}
            />
          </Modal>
        )
      }
    }

    if (page === Pages.Status || experiments.instant_dashing_v2) {
      return (
        <div>
          <Text styles={Text.Styles.Bold24}>{`${t.greeting()} ${
            this.applicant.firstName
          }`}</Text>
          <Stack size={Stack.Sizes.XxSmall}>
            <Text style={Text.Styles.SectionSubtext}>{t.subTitle()}</Text>
          </Stack>
          <Separator
            verticalMargin={Separator.Sizes.Small}
            type={Separator.Types.FullBorder}
          />

          {Steps.map((StepComponent, i) => {
            const currentStep = this.currentStep()
            const currentStepKey = StepComponent.props.completeKey
            const index = i + 1

            const stepData = {
              key: i,
              step: index,
              title: StepComponent.props.generateTitle,
              isComplete: onboardingProgress[currentStepKey],
              isActive: index === currentStep,
              stepName: StepComponent.props.step,
              trackClick: trackConfirmationPageSection,
              badgeText: StepComponent.props.badgeText,
            }

            if (
              currentStepKey === ConfirmationStepKeys.FASTPAY &&
              onboardingProgress[ConfirmationStepKeys.FASTPAY]
            ) {
              stepData.isActive = false
              stepData.isComplete = true
            }

            if (
              currentStepKey === ConfirmationStepKeys.ACTIVATIONKIT &&
              (shippingStatus === ShippingStatus.Delivered ||
                onboardingProgress[ConfirmationStepKeys.REDCARDACTIVATION])
            ) {
              stepData.isComplete = true
            }

            if (experiments.bank_onboarding) {
              // Keeps the add bank account on if user have not completed it
              // Edge case for when red card is delivered but user has
              // not submitted payment info
              if (
                currentStepKey === ConfirmationStepKeys.PAYMENTACCOUNTSETUP &&
                !stepData.isComplete &&
                onboardingProgress[ConfirmationStepKeys.BACKGROUNDCHECK]
              ) {
                stepData.isActive = true
              }
            }

            if (currentStepKey === ConfirmationStepKeys.STARTDASHING) {
              if (onboardingProgress[ConfirmationStepKeys.BACKGROUNDCHECK]) {
                stepData.isActive = true
                stepData.isComplete = false
              }
              if (
                (experiments.bank_onboarding &&
                  experiments.instant_dashing_v2 &&
                  !onboardingProgress[
                    ConfirmationStepKeys.PAYMENTACCOUNTSETUP
                  ]) ||
                (!experiments.bank_onboarding &&
                  !experiments.instant_dashing_v2 &&
                  !onboardingProgress[ConfirmationStepKeys.REDCARDACTIVATION])
              ) {
                stepData.isActive = false
                stepData.isComplete = false
              }
            }

            return (
              <ListItem {...stepData}>
                {React.cloneElement(StepComponent, stepData)}
              </ListItem>
            )
          })}
        </div>
      )
    }

    return (
      <RedCard
        uniqueLink={this.applicant.uniqueLink}
        application={this.props.application}
        dasherId={dasherId}
        returnLink={this.returnLink}
      />
    )
  }

  private setStripe = (stripe: any) => {
    this.setState({
      stripe,
    })
  }
}

const mapStateToProps = state => ({
  application: state.application,
  isLoading: state.applicationStatus.isLoading,
  applicationStatus: state.applicationStatus.data,
  experiments: state.experiments,
  dasherId: state.application.dasherId,
  stripeCredentials: state.stripeCredentials,
  paymentAccount: state.paymentAccount,
})

const mapDispatchToProps = {
  getApplicationStatus: actions.getApplicationStatus,
  trackConfirmationPageRendered: events.trackConfirmationPageRendered,
  getStripeCredentials: actions.getStripeCredentials,
  getPaymentAccount: actions.getPaymentAccount,
  updateBankAccount: actions.updateBankAccount,
  trackConfirmationPageSection: events.trackConfirmationPageSectionClicked,
  trackDropshippingEvent: events.trackDropshippingEvent,
}

export const DisconnectedDropshippingStatus = DropshippingStatus

export default connect(mapStateToProps, mapDispatchToProps)(DropshippingStatus)
